<template>

  <div>

    <!-- @vue-ignore: TypeScript thinks that d is a string, but it's a Vuetify DateInstance -->
    <v-date-input
      :label="$t('titles.date')"
      :model-value="formState.date ? vuetifyDateHelper.date(formState.date.toString() + 'T00:00:00') : null"
      @update:model-value="d => formState.date = Temporal.PlainDate.from({year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate()})"
      :rules="[rules!.required!()]"
      :min="today.toString()"
    />

    <v-alert v-if="!formState.date">
      Please select a date
    </v-alert>

    <template v-if="formState.date">

      <v-table density="compact">
        <thead>
          <tr>
            <th scope="col" class="w-25">Venue</th>
            <th scope="col" class="w-45">Event</th>
            <th scope="col" class="w-30" colspan="2">Event Pricing</th>
          </tr>
        </thead>
        <tbody>

          <template v-if="loadingDateSpecificData">
            <tr>
              <td colspan="4" class="py-4">
                <v-progress-circular indeterminate class="d-block mx-auto" />
              </td>
            </tr>
          </template>

          <template v-else>

            <template v-if="!formState.venues.length">
              <tr>
                <td colspan="4" class="py-4 text-center">
                  there are no events for the selected date and product
                </td>
              </tr>
            </template>

            <template v-for="venue in formState.venues">
              <tr v-for="(event, i) in venue.events" style="vertical-align: top">
                <td v-if="i === 0" :rowspan="venue.events.length" class="py-3">
                  <p>
                    {{ venue.name }}
                    <record-id :id="venue.id" />
                  </p>
                  <p class="mt-2">
                    <template v-if="!venue.venueTiers">
                      <template v-if="venue.defaultPrice">
                        {{ currencyFormatter.format(venue.defaultPrice) }}
                      </template>
                    </template>
                    <template v-if="venue.venueTiers">
                      <template v-for="venueTier in venue.venueTiers">
                        {{ currencyFormatter.format(venueTier.price) }}
                        up to {{ percentageFormatter.format(venueTier.percentage / 100) }}<br>
                      </template>
                    </template>
                  </p>

                </td>
                <td class="py-3">
                  <p>
                    {{ event.name }}
                    <record-id :id="event.id" />
                  </p>
                  <p class="mt-2">
                    {{ event.startLocal.toPlainTime().toLocaleString('en-US', {timeStyle: 'short'}) }} –
                    {{ event.endLocal.toPlainTime().toLocaleString('en-US', {timeStyle: 'short'}) }}
                  </p>
                </td>
                <td>
                  <v-checkbox v-model="event.useEventTiers" density="compact" />
                </td>
                <td class="py-3">

                  <table v-if="event.useEventTiers" class="event-tiers-table">
                    <tfoot>
                      <tr>
                        <td colspan="4" class="text-end">
                          <v-btn
                            icon="$create"
                            size="sm"
                            @click="event.eventTiers.push({id: null, order: Math.max(...event.eventTiers.map(et => et.order)) + 1, price: null, percentage: 100})"
                            :disabled="event.eventTiers.length >= 5"
                          />
                        </td>
                      </tr>
                    </tfoot>
                    <tbody>
                      <tr v-for="(eventTier, i) in event.eventTiers" :key="eventTier.order">
                        <td>
                          <v-number-input
                            density="compact"
                            :label="$t('titles.tier_n', i + 1)"
                            v-model="eventTier.price"
                            :min="0.01"
                            :precision="2"
                            prefix="$"
                            hide-details
                            :rules="[rules!.required!()]"
                          />
                        </td>
                        <td>
                          <v-number-input
                            density="compact"
                            :label="$t('titles.from')"
                            :precision="0"
                            :min="i === 0 ? 0 : 1"
                            :model-value="i === 0 ? 0 : event.eventTiers[i-1].percentage"
                            suffix="%"
                            readonly
                            hide-details
                          />
                        </td>
                        <td>
                          <v-number-input
                            density="compact"
                            :label="$t('titles.to')"
                            v-model="eventTier.percentage"
                            :precision="0"
                            :min="1"
                            :max="100"
                            suffix="%"
                            hide-details
                            :readonly="i === event.eventTiers.length - 1"
                            :rules="[rules!.required!(), thisTierPercentage => isLessThanNextEventTier(event, thisTierPercentage, i)]"
                          />
                        </td>
                        <td class="text-end">
                          <v-btn
                            icon="$remove"
                            size="sm"
                            @click="event.eventTiers.splice(i, 1)"
                            :disabled="event.eventTiers.length < 2"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                </td>
              </tr>
            </template>

          </template>

        </tbody>
      </v-table>

    </template>

  </div>

</template>

<script setup lang="ts">

import { computed, onBeforeMount, reactive, ref, watchEffect } from 'vue'
import { useDate } from 'vuetify'
import { useRules } from 'vuetify/labs/rules'

// components
import { VDateInput } from 'vuetify/labs/VDateInput'

// models
import { DefaultRate, type InventoryWithEventTiers } from '../../../../../models/DefaultRate'
import Event, { type GetEventsByLocationResponseItem } from '../../../../../models/Event'
import { EventTier, type MutateEventTiersRequest } from '../../../../../models/EventTier'
import Location from '../../../../../models/Location'
import LocationProduct from '../../../../../models/LocationProduct'
import { VenueLocationProduct } from '../../../../../models/VenueLocationProduct'

// misc
import { Temporal } from '@js-temporal/polyfill'
import NotificationService from '../../../../../services/NotificationService'
import PermissionsService from '../../../../../services/PermissionsService'
import PreferencesService from '../../../../../services/PreferencesService'
import { currencyFormatter, percentageFormatter } from '../../../../../utils/formatters'
import { Inventory } from '../../../../../models/Inventory'

/**
 * Props
 */

interface Props {
  location: Location
  locationProduct: LocationProduct
  date?: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  date: null,
})

/**
 * Composables and stuff
 */

const vuetifyDateHelper = useDate()
const rules = useRules()

// @ts-ignore
const showIds = computed<boolean>(() => {
  return PreferencesService.preferences.showIds && PermissionsService.can('seeids')
})

const emit = defineEmits(['submitted'])
const today = Temporal.Now.plainDateISO()

/**
 * Initialization
 */

onBeforeMount(() => {
  if (props.date) {
    formState.date = props.date
  }
})

/**
 * Form state
 */

interface FormState {
  date: null | Temporal.PlainDate
  venues: FormVenue[]
}

interface FormVenue {
  readonly id: number
  readonly name: string
  readonly defaultPrice: null | number
  readonly venueTiers: {
    readonly id: number
    readonly price: number
    readonly percentage: number
  }[]
  readonly events: FormEvent[]
}

interface FormEvent {
  readonly id: number
  readonly name: string
  readonly startLocal: Temporal.PlainDateTime
  readonly endLocal: Temporal.PlainDateTime
  oldEventTiers: InventoryWithEventTiers['eventTiers']
  useEventTiers: boolean
  eventTiers: FormEventTier[]
}

interface FormEventTier {
  readonly id: null | number
  price: null | number
  percentage: null | number
  order: number
}

const formState = reactive<FormState>({
  date: null,
  venues: [],
})

/**
 *
 */

const loadingDateSpecificData = ref<boolean>(false)

interface DateSpecificData {
  date: null | Temporal.PlainDate,
  events: GetEventsByLocationResponseItem[]
  inventoriesWithEventTiers: InventoryWithEventTiers[]
}

const dateSpecificData: DateSpecificData = reactive({
  date: null,
  events: [],
  inventoriesWithEventTiers: [],
})

watchEffect(async () => {

  if (!formState.date) {
    // no date is selected in the modal
    if (dateSpecificData.date) {
      dateSpecificData.date = null
      dateSpecificData.events = []
      dateSpecificData.inventoriesWithEventTiers = []
    }
    return
  }

  if (dateSpecificData.date && Temporal.PlainDate.compare(formState.date, dateSpecificData.date) === 0) {
    // we already have data for the selected date
    return
  }

  loadingDateSpecificData.value = true

  let loadedEvents: GetEventsByLocationResponseItem[] = []
  let loadedInventories: InventoryWithEventTiers[] = []

  const loadEvents = Event
    .getByLocationId(props.location.ID, {start: formState.date, end: formState.date})
    .then((events: GetEventsByLocationResponseItem[]) => {
      loadedEvents = events
    })

  const loadEventTiers = DefaultRate
    .getByLocationId({
      locationId: props.location.ID,
      vertical: 'event',
      start: formState.date,
      end: formState.date,
      includeEventTiers: true,
    }).then(response => {
      loadedInventories = response.inventories_with_event_tiers ?? []
    })

  try {
    await Promise.all([loadEvents, loadEventTiers])
    dateSpecificData.date = formState.date
    dateSpecificData.events = loadedEvents
    dateSpecificData.inventoriesWithEventTiers = loadedInventories
  } catch(e) {
    console.error(e)
    NotificationService.error('Couldn\'t load events!')
  }

  loadingDateSpecificData.value = false

})

watchEffect(async () => {

  const venueIdsWithEvents = new Set(dateSpecificData.events.map(e => e.venueID))

  const venueLocationProducts = VenueLocationProduct.query()
    .where('locationProductID', props.locationProduct.ID)
    .where('venueID', (value: number) => venueIdsWithEvents.has(value))
    .with('venue')
    .with('venueTiers', query => { query.orderBy('tierOrder') })
    .get()
    .toSorted((vlp1, vlp2) => {
      if (vlp1.venue?.name === vlp2.venue?.name) { return 0 }
      return vlp1.venue?.name > vlp2.venue?.name ? 1 : -1
    })

  const formVenues: FormVenue[] = venueLocationProducts.map(vlp => {

    const venueTiers = vlp.venueTiers ?? []
    const events = dateSpecificData.events.filter(e => e.venueID === vlp.venueID)

    const formVenue: FormVenue = {
      id: vlp.venueID,
      name: events[0].venueName,
      defaultPrice: vlp.defaultPrice,
      venueTiers: venueTiers.map(vt => {
        return {
          id: vt.ID,
          price: vt.tierPrice,
          percentage: vt.tierInventoryPercentage * 100,
        }
      }),
      events: events.map(event => {

        const eventInventory = dateSpecificData.inventoriesWithEventTiers
          .find(iwet => iwet.locationProductID === props.locationProduct.ID && iwet.eventID === event.ID)

        const rawEventTiers = eventInventory?.eventTiers ?? []
        let eventTiers: FormEventTier[]
        let useEventTiers = false

        if (rawEventTiers.length) {
          rawEventTiers.sort((et1, et2) => et1.tierOrder - et2.tierOrder)
          eventTiers = rawEventTiers.map(et => {
            return {
              id: et.ID,
              price: et.tierPrice,
              percentage: et.tierInventoryPercentage * 100,
              order: et.tierOrder,
            }
          })
          useEventTiers = true
        } else {
          eventTiers = [{
            id: null,
            price: null,
            percentage: 100,
            order: 1,
          }]
        }

        return {
          id: event.ID,
          name: event.name,
          startLocal: event.eventStartLocal,
          endLocal: event.eventEndLocal,
          oldEventTiers: rawEventTiers,
          useEventTiers: useEventTiers,
          eventTiers: eventTiers,
        }

      }),
    }

    return formVenue

  })

  formState.venues = formVenues

})

/**
 * Submission
 */

const submitting = ref<boolean>(false)

async function submit() {

  if (!formState.date) {
    return
  }

  submitting.value = true

  // The "mutate event tiers" endpoint requires an inventory ID, so fetch inventories for the selected date and LP
  const inventories = await Inventory.getByLocationId(props.location.ID, {
    vertical: 'event',
    start: formState.date!,
    end: formState.date!,
  })

  const request: MutateEventTiersRequest = {operations: []}

  formState.venues.forEach(venue => {
    venue.events.forEach(event => {

      // only submit tiers for events which have been changed

      let hasChanged = false
      const newTiers = event.useEventTiers ? event.eventTiers.toSorted((et1, et2) => (et1.percentage ?? 0) - (et2.percentage ?? 0)): []
      const oldTiers = event.oldEventTiers.toSorted((et1, et2) => et1.tierInventoryPercentage - et2.tierInventoryPercentage)

      if (newTiers.length !== oldTiers.length) {
        hasChanged = true
      } else {
        hasChanged = newTiers.some((newTier, i) => {
          const oldTier = oldTiers[i]
          return newTier.price !== oldTier.tierPrice || newTier.percentage !== oldTier.tierInventoryPercentage
        })
      }

      if (!hasChanged) {
        return
      }

      const inventory = inventories.find(inventory => {
        return (
          inventory.locationProductID === props.locationProduct.ID &&
          inventory.eventID === event.id &&
          Temporal.PlainDate.compare(inventory.date, formState.date!) === 0
        )
      })

      if (!inventory) {
        console.warn(`No inventory found for event ${event.id}`)
        NotificationService.warn(`No inventory found for event ${event.id}`)
        return
      }

      request.operations.push({
        inventory_id: inventory.ID,
        tiers : newTiers.map(tier => {
          return {
            price: tier.price!,
            inventory_percentage: tier.percentage! / 100,
          }
        })
      })

    })
  })

  if (request.operations.length) {

    EventTier.mutate(request)
      .then(() => {
        // TODO: do we need to do anything with the response here?
        NotificationService.success('Event rates updated')
        emit('submitted')
      })
      .catch(e => {
        console.error(e)
        NotificationService.error('Couldn\'t update event rates!')
      })
      .finally(() => {
        submitting.value = false
      })

  } else {

    submitting.value = false
    NotificationService.info('No event rates changed')
    emit('submitted')

  }

}

defineExpose({ submit, submitting })

function isLessThanNextEventTier(event: FormEvent, thisTierPercentage: any, thisTierIndex: number) {

  if (!thisTierPercentage) {
    return true
  }

  const nextTier = event.eventTiers.at(thisTierIndex + 1)

  if (!nextTier || !nextTier.percentage || thisTierPercentage < nextTier.percentage) {
    return true
  }

  return `Must be less than ${nextTier.percentage}`

}

</script>

<style lang="scss">

.event-tiers-table {

  tr {
    th, td {

      padding-top: 4px;
      padding-bottom: 4px;

      &:not(:first-child) {
        padding-left: 3px;
      }

      &:not(:last-child) {
        padding-right: 3px;
      }

    }
  }

  .v-number-input {
    min-width: 100px;
  }

}

</style>

<script setup lang="ts">
</script>